import { useEffect, useState } from 'react'
import type { AgendamentoDto } from '@crm/shared'
import {
  useAgendamentosDoContato,
  useCancelarAgendamento,
  useRestaurarAgendamento,
} from '../../api/hooks-operacao'
import { formatarDataHora } from '../../formato'
import { juntar } from '../../componentes/ui'

/**
 * O que ainda vai sair sozinho para este cliente.
 *
 * Mensagem agendada é a única coisa que fala com o cliente sem ninguém
 * digitar na hora. Quando ele compra, o vendedor precisa poder parar o
 * "ainda tem interesse?" que estava marcado para quinta — senão o cliente
 * recebe, depois de pagar, uma cobrança de decisão que já tomou.
 *
 * É fila, e não histórico. A mensagem que já saiu está na própria conversa,
 * logo ao lado; repeti-la aqui fazia o bloco crescer para sempre com cartões
 * que não pedem nada de ninguém.
 */

/**
 * Quanto tempo o desmarcado fica recuperável antes de sumir da lista.
 *
 * Desmarcar é um clique só, sem tela de confirmação. Sem esta janela, o erro
 * de mira só apareceria no dia em que o cliente não recebesse — e aí não há
 * mais o que fazer. Seis segundos dão tempo de ler o que acabou de acontecer
 * e voltar atrás, sem deixar um aviso pendurado na tela.
 */
const SEGUNDOS_PARA_RECUPERAR = 6

const ROTULO: Record<AgendamentoDto['status'], string> = {
  pendente: 'será enviado',
  enviada: 'enviada',
  cancelada: 'desmarcada',
  erro: 'falhou',
}

const COR: Record<AgendamentoDto['status'], string> = {
  pendente: 'bg-amber-100 text-amber-900',
  enviada: 'bg-green-100 text-green-800',
  cancelada: 'bg-tinta-100 text-tinta-500',
  erro: 'bg-red-100 text-red-800',
}

export function AgendamentosDoContato({ contatoId }: { contatoId: string }) {
  const { data, isLoading } = useAgendamentosDoContato(contatoId)

  /*
    Qual desmarcado ainda está na janela de arrependimento.

    Mora aqui, e não na linha, porque é esta lista que decide quem aparece: o
    desmarcado só continua na tela enquanto for recuperável.
  */
  const [recuperavel, setRecuperavel] = useState<string | null>(null)

  if (isLoading) return <p className="text-sm text-tinta-400">Carregando…</p>

  /*
    Fica o que ainda pede alguma coisa de alguém.

    - **pendente**: ainda vai sair, e dá para desmarcar;
    - **falhou**: não chegou ao cliente, e alguém precisa saber;
    - **desmarcada**: só durante os seis segundos de voltar atrás.

    Enviada sai da lista: já está na conversa, e um cartão por mensagem que
    saiu viraria, com o tempo, um segundo histórico que ninguém lê. Quem
    precisa da trilha completa tem o log do sistema.
  */
  const itens = (data?.itens ?? []).filter(
    (a) =>
      a.status === 'pendente' ||
      a.status === 'erro' ||
      (a.status === 'cancelada' && a.id === recuperavel),
  )

  if (itens.length === 0) {
    return <p className="text-sm text-tinta-400">Nenhuma mensagem programada para este cliente.</p>
  }

  /*
    Pendente em cima, na ordem em que vai sair; embaixo, o que falhou.

    A pergunta que traz a pessoa aqui é "o que ainda vai sair?". A falha
    fica logo abaixo porque é rara e precisa ser vista, mas não é o que se
    procura primeiro.
  */
  const pendentes = itens
    .filter((a) => a.status === 'pendente')
    .sort((a, b) => a.agendadoPara.localeCompare(b.agendadoPara))
  const passados = itens
    .filter((a) => a.status !== 'pendente')
    .sort((a, b) => b.agendadoPara.localeCompare(a.agendadoPara))

  return (
    <ul className="space-y-2">
      {[...pendentes, ...passados].map((agendamento) => (
        <Linha
          key={agendamento.id}
          agendamento={agendamento}
          recuperavel={agendamento.id === recuperavel}
          aoDesmarcar={() => setRecuperavel(agendamento.id)}
          aoFecharJanela={() => setRecuperavel(null)}
        />
      ))}
    </ul>
  )
}

function Linha({
  agendamento,
  recuperavel,
  aoDesmarcar,
  aoFecharJanela,
}: {
  agendamento: AgendamentoDto
  /** Acabou de ser desmarcado e ainda dá para voltar atrás. */
  recuperavel: boolean
  aoDesmarcar: () => void
  aoFecharJanela: () => void
}) {
  const cancelar = useCancelarAgendamento()
  const restaurar = useRestaurarAgendamento()
  const [mantida, setMantida] = useState(false)
  const [restam, setRestam] = useState(SEGUNDOS_PARA_RECUPERAR)

  // O "mantida" só existe para o clique no verde ter resposta. Some sozinho:
  // é confirmação do que já estava certo, não um estado para administrar.
  useEffect(() => {
    if (!mantida) return
    const t = setTimeout(() => setMantida(false), 2000)
    return () => clearTimeout(t)
  }, [mantida])

  /*
    Dois relógios: um anda o contador na tela, o outro fecha a janela.

    Separados de propósito. Fechar dentro do contador significaria avisar o
    componente de cima a partir de um `setState`, que o React pode repetir —
    e o aviso sairia duas vezes.
  */
  useEffect(() => {
    if (!recuperavel) return
    setRestam(SEGUNDOS_PARA_RECUPERAR)
    const tique = setInterval(() => setRestam((s) => Math.max(0, s - 1)), 1000)
    const fim = setTimeout(() => aoFecharJanela(), SEGUNDOS_PARA_RECUPERAR * 1000)
    return () => {
      clearInterval(tique)
      clearTimeout(fim)
    }
  }, [recuperavel])

  const pendente = agendamento.status === 'pendente'

  return (
    <li
      className={juntar(
        'rounded-md px-2.5 py-2 text-sm ring-1',
        recuperavel
          ? 'bg-tinta-50 ring-tinta-300'
          : pendente
            ? 'bg-amber-50 ring-amber-200'
            : 'bg-superficie ring-tinta-200',
      )}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs text-tinta-600">{formatarDataHora(agendamento.agendadoPara)}</span>
        <span
          className={juntar(
            'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium',
            COR[agendamento.status],
          )}
        >
          {ROTULO[agendamento.status]}
        </span>
      </div>

      {/*
        O texto aparece cortado em três linhas em vez de inteiro.

        A ficha é uma coluna estreita ao lado da conversa, e uma mensagem de
        lote com quatro parágrafos empurraria as etiquetas e a observação para
        fora da tela. Quem precisa ler tudo abre a lista de agendamentos.
      */}
      <p className="line-clamp-3 whitespace-pre-wrap text-tinta-700">{agendamento.conteudo}</p>

      <p className="mt-1 text-xs text-tinta-400">
        Por {agendamento.criadoPorNome}
        {agendamento.loteId ? ' · disparo em lote' : ''}
      </p>

      {agendamento.status === 'erro' && agendamento.erro ? (
        <p className="mt-1 text-xs text-red-700">{agendamento.erro}</p>
      ) : null}

      {recuperavel ? (
        /*
          A janela de arrependimento. O contador fica à vista para a pressa ser
          do tamanho certo: sem o número, ou a pessoa corre à toa, ou descobre
          que acabou quando o aviso já sumiu.
        */
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={restaurar.isPending}
            onClick={() => restaurar.mutate(agendamento.id, { onSuccess: aoFecharJanela })}
            className="rounded bg-green-600 px-2 py-0.5 text-xs font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
          >
            {restaurar.isPending ? 'recuperando…' : 'Recuperar'}
          </button>
          <span className="text-xs text-tinta-500">some em {restam}s</span>

          {restaurar.isError ? (
            <p className="w-full text-xs text-red-700">{(restaurar.error as Error).message}</p>
          ) : null}
        </div>
      ) : pendente ? (
        /**
         * A pergunta e os dois botões ficam à vista o tempo todo.
         *
         * Antes o cartão mostrava só um "Desmarcar" discreto, e a pergunta
         * aparecia depois do clique. Quem abre a ficha porque o cliente acabou
         * de comprar já vem decidido: o passo a mais não protegia nada, só
         * escondia a saída atrás de um link que nem parecia botão.
         *
         * O que ficou no lugar da trava é a volta atrás: desmarcar age na
         * hora, e os seis segundos seguintes desfazem.
         */
        <div className="mt-2 space-y-1.5">
          <p className="text-xs text-tinta-600">Não mandar esta mensagem?</p>

          {/*
            Os dois botões têm a mesma forma, e só a cor muda: verde para
            manter, à esquerda, e vermelho para desmarcar, à direita. Enquanto
            um dos lados era texto simples, o vermelho era o único botão de
            verdade ali e parecia o caminho esperado.
          */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setMantida(true)}
              className="rounded bg-green-600 px-2 py-0.5 text-xs font-semibold text-white transition hover:bg-green-700"
            >
              Manter
            </button>
            <button
              type="button"
              disabled={cancelar.isPending}
              onClick={() => cancelar.mutate(agendamento.id, { onSuccess: aoDesmarcar })}
              className="rounded bg-red-600 px-2 py-0.5 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
            >
              {cancelar.isPending ? 'desmarcando…' : 'Desmarcar'}
            </button>

            {mantida ? <span className="text-xs text-green-700">mantida</span> : null}
          </div>

          {cancelar.isError ? (
            <p className="text-xs text-red-700">{(cancelar.error as Error).message}</p>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}
